import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const PRODUCT_OPTIONS = [
  "Livestream Manager",
  "Live Intelligence",
  "AI Chat",
] as const;

const VOLUME_OPTIONS = [
  "0 – 10 per month",
  "11 – 30 per month",
  "31 – 100 per month",
  "100+ per month",
] as const;

interface DemoRequestDialogProps {
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type FormErrors = Partial<Record<
  "company_name" | "pic_name" | "email" | "phone" | "livestream_volume" | "products_of_interest",
  boolean
>>;

export function DemoRequestDialog({ children, open: openProp, onOpenChange }: DemoRequestDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [form, setForm] = useState({
    company_name: "",
    pic_name: "",
    email: "",
    phone: "",
    livestream_volume: "",
    products_of_interest: [] as string[],
  });

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
  };

  const toggleProduct = (product: string) => {
    setForm((prev) => {
      const updated = prev.products_of_interest.includes(product)
        ? prev.products_of_interest.filter((p) => p !== product)
        : [...prev.products_of_interest, product];
      if (updated.length > 0) setErrors((prev2) => ({ ...prev2, products_of_interest: false }));
      return { ...prev, products_of_interest: updated };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const newErrors: FormErrors = {
      company_name: !form.company_name.trim(),
      pic_name: !form.pic_name.trim(),
      email: !form.email.trim() || !emailRegex.test(form.email.trim()),
      phone: !form.phone.trim(),
      livestream_volume: !form.livestream_volume,
      products_of_interest: form.products_of_interest.length === 0,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      toast.error("Please fill in all required fields.");
      return;
    }

    setLoading(true);
    try {
      const nameParts = form.pic_name.trim().split(" ");
      await api.post("/api/v1/app/get-started", {
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(" ") || nameParts[0],
        email: form.email.trim().slice(0, 255),
        company_name: form.company_name.trim().slice(0, 200),
        phone: form.phone.trim().slice(0, 30) || undefined,
        livestream_volume: form.livestream_volume || undefined,
        products_of_interest: form.products_of_interest,
        source: "demo_request",
      });

      setForm({
        company_name: "",
        pic_name: "",
        email: "",
        phone: "",
        livestream_volume: "",
        products_of_interest: [],
      });
      setErrors({});
      setOpen(false);
      toast.success("Thanks! We'll be in touch soon.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children ? <DialogTrigger asChild>{children}</DialogTrigger> : null}
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:w-full sm:max-w-[520px] rounded-lg bg-background border-border p-0 gap-0 max-h-[calc(100dvh-2rem)] sm:max-h-[90dvh] overflow-y-auto top-[max(1rem,env(safe-area-inset-top,1rem))] translate-y-0 sm:top-1/2 sm:-translate-y-1/2">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle className="text-[18px] font-medium tracking-[-0.02em]">Request a demo</DialogTitle>
          <p className="text-[13px] text-muted-foreground mt-1">
            Tell us about your company and we'll reach out within 24 hours.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4">
          {/* Company name */}
          <div className="space-y-1.5">
            <Label htmlFor="company_name" className="text-[13px]">Company name *</Label>
            <Input
              id="company_name"
              value={form.company_name}
              onChange={(e) => handleChange("company_name", e.target.value)}
              placeholder="e.g. PT Tokopedia"
              maxLength={200}
              className={`h-9 text-[13px] ${errors.company_name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            />
          </div>

          {/* Person in charge */}
          <div className="space-y-1.5">
            <Label htmlFor="pic_name" className="text-[13px]">Person in charge *</Label>
            <Input
              id="pic_name"
              value={form.pic_name}
              onChange={(e) => handleChange("pic_name", e.target.value)}
              placeholder="Full name"
              maxLength={200}
              className={`h-9 text-[13px] ${errors.pic_name ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            />
          </div>

          {/* Email + Phone side by side */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[13px]">Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="you@company.com"
                maxLength={255}
                className={`h-9 text-[13px] ${errors.email ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone" className="text-[13px]">Phone *</Label>
              <Input
                id="phone"
                type="tel"
                value={form.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="+62 812 345 6789"
                maxLength={30}
                className={`h-9 text-[13px] ${errors.phone ? "border-red-500 focus-visible:ring-red-500" : ""}`}
              />
            </div>
          </div>

          {/* Livestream volume */}
          <div className="space-y-1.5">
            <Label className="text-[13px]">Current livestream volume *</Label>
            <Select value={form.livestream_volume} onValueChange={(v) => handleChange("livestream_volume", v)}>
              <SelectTrigger className={`h-9 text-[13px] ${errors.livestream_volume ? "border-red-500 focus-visible:ring-red-500" : ""}`}>
                <SelectValue placeholder="Select volume" />
              </SelectTrigger>
              <SelectContent>
                {VOLUME_OPTIONS.map((opt) => (
                  <SelectItem key={opt} value={opt} className="text-[13px]">{opt}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Products of interest */}
          <div className="space-y-2">
            <Label className="text-[13px]">Products of interest *</Label>
            <div className={`flex flex-col sm:flex-row sm:flex-wrap gap-x-6 gap-y-2.5 rounded-md p-2 transition-colors ${errors.products_of_interest ? "border border-red-500" : ""}`}>
              {PRODUCT_OPTIONS.map((product) => (
                <label key={product} className="flex items-center gap-2 cursor-pointer group">
                  <div
                    className={`h-4 w-4 shrink-0 border rounded-[3px] flex items-center justify-center transition-colors ${
                      form.products_of_interest.includes(product)
                        ? "bg-foreground border-foreground"
                        : errors.products_of_interest
                          ? "border-red-500 group-hover:border-red-400"
                          : "border-muted-foreground/40 group-hover:border-muted-foreground/70"
                    }`}
                    onClick={() => toggleProduct(product)}
                    onKeyDown={(e) => { if (e.key === " " || e.key === "Enter") toggleProduct(product); }}
                    role="checkbox"
                    aria-checked={form.products_of_interest.includes(product)}
                    tabIndex={0}
                  >
                    {form.products_of_interest.includes(product) && (
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className="text-background">
                        <path d="M8.5 2.5L3.5 7.5L1.5 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-[13px] select-none" onClick={() => toggleProduct(product)}>{product}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 h-10 text-[14px] font-medium bg-foreground text-background hover:bg-foreground/90 transition-colors disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                Submit request
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
